import type { Workorder } from '@certhon/models';

// Re-export date/time utilities from common location
export { formatHours, formatDate, formatTime } from '@/utils/date';

// Re-export settlement utilities from models package
export {
  SETTLEMENT_LABELS as SETTLEMENT_DISPLAY,
  getSettlementLabel as getSettlementName,
} from '@certhon/models';

// ============================================================================
// Edit permissions
// ============================================================================

/**
 * Why a workorder cannot be edited by the given user, or `null` when it can.
 *
 * A workorder is frozen (description, notes, flags, labor, materials, and
 * completion) when it is completed, not yet scheduled (concept), cancelled,
 * or assigned to someone else. This is the single source of truth used by
 * the details, sign, labor and material panels so they behave consistently.
 */
export function getWorkorderLockReason(
  workorder: Pick<
    Workorder,
    'completed' | 'progress' | 'assigned_user_id' | 'technician_name'
  >,
  currentUserId: number | null | undefined,
): string | null {
  if (workorder.completed) {
    return 'This workorder is completed and can no longer be edited.';
  }
  if (workorder.progress === 'concept') {
    return 'This workorder is still a concept and cannot be edited until it is scheduled.';
  }
  if (workorder.progress === 'cancelled') {
    return 'This workorder is cancelled and cannot be edited.';
  }
  if (currentUserId == null || workorder.assigned_user_id !== currentUserId) {
    return workorder.technician_name
      ? `This workorder is assigned to ${workorder.technician_name}; only the assigned technician can edit it.`
      : 'Only the assigned technician can edit this workorder.';
  }
  return null;
}

export function isWorkorderEditable(
  workorder: Parameters<typeof getWorkorderLockReason>[0],
  currentUserId: number | null | undefined,
): boolean {
  return getWorkorderLockReason(workorder, currentUserId) === null;
}
